import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {IssueCommentItem} from './IssueCommentItem'
import {ReviewSummaryItem} from './ReviewSummaryItem'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {CommentsZeroState} from './CommentsZeroState'
import type {ConversationItem} from './utils/build-conversation-items'
import {ThreadPreview} from './ThreadPreview'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import {useLoadAllIssueCommentsAndReviews} from './use-load-all-issue-comments-and-reviews'

export interface IssueCommentsContentProps {
  pathName: string
  pullRequestAuthorLogin: string
  filteredConversationItems: ConversationItem[]
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
  totalConversationItemsCount: number
}

/**
 * Data loading container that fetches issue comments and review summaries.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
export function IssueCommentsContent({
  pathName,
  pullRequestAuthorLogin,
  filteredConversationItems,
  onQuoteReply,
  onNavigateToDiffComment,
  totalConversationItemsCount,
}: IssueCommentsContentProps) {
  const isLoadingMore = useLoadAllIssueCommentsAndReviews(pathName)

  return (
    <IssueCommentsComponent
      filteredConversationItems={filteredConversationItems}
      totalConversationItemsCount={totalConversationItemsCount}
      isLoadingMore={isLoadingMore}
      pullRequestAuthorLogin={pullRequestAuthorLogin}
      onQuoteReply={onQuoteReply}
      onNavigateToDiffComment={onNavigateToDiffComment}
    />
  )
}

interface IssueCommentsComponentProps {
  filteredConversationItems: ConversationItem[]
  totalConversationItemsCount: number
  isLoadingMore: boolean
  pullRequestAuthorLogin: string
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
}

/**
 * Presentational component that renders issue comments and review summaries.
 * Handles the display logic for comments, loading states, and zero states.
 */
function IssueCommentsComponent({
  filteredConversationItems,
  totalConversationItemsCount,
  isLoadingMore,
  pullRequestAuthorLogin,
  onQuoteReply,
  onNavigateToDiffComment,
}: IssueCommentsComponentProps) {
  const canReact = isLoggedIn()
  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  const hasConversationItems = totalConversationItemsCount > 0

  useEffect(() => {
    // Only announce when we've finished loading all comments
    if (isLoadingMore) return

    const announceText =
      filteredConversationItems.length > 0
        ? `${filteredConversationItems.length} ${filteredConversationItems.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isLoadingMore, debouncedAnnounce, filteredConversationItems])

  const unassignedThreads = useMemo(
    () => filteredConversationItems.filter(item => item.type === 'thread').map(item => item.data),
    [filteredConversationItems],
  )

  return (
    <>
      {filteredConversationItems.length > 0 ? (
        <ErrorBoundary boundaryName="IssueCommentsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            <HydrateThreadPositioning threads={unassignedThreads} />
            <div className="d-flex flex-column position-relative width-full gap-3">
              {filteredConversationItems.map(item =>
                // TODO: limit/page the rendered items so that we don't try to hydrate thread positioning for every
                // thread at once
                item.type === 'review-summary' ? (
                  <ReviewSummaryItem
                    key={item.key}
                    reviewSummary={item.data.summary}
                    reviewThreads={item.data.threads}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                    withReviewStateIcon
                    onNavigateToDiffComment={onNavigateToDiffComment}
                  />
                ) : item.type === 'thread' ? (
                  <ThreadPreview
                    key={item.key}
                    thread={item.data}
                    selectedReviewId={null}
                    onNavigateToDiffComment={onNavigateToDiffComment}
                    headingLevel="h2"
                  />
                ) : (
                  <IssueCommentItem
                    key={item.key}
                    comment={item.data}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                  />
                ),
              )}
              {isLoadingMore && <ThreadPreviewSkeleton count={1} />}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : isLoadingMore ? (
        <ThreadPreviewSkeleton />
      ) : (
        <CommentsZeroState hasItems={hasConversationItems} />
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
